import { useState } from 'react'
import { StaffFilter } from '@/components/staff-filter'
import { ProjectsTable, ProjectsTableSkeleton } from '@/components/projects-table'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { segments } from '@/features/home/model'
import { StatusBar } from '@/features/home/status-bars'
import { useMyProjectsFilter } from '@/hooks/use-my-projects-filter'
import { parseUTCDate } from '@/lib/utils'
import { type Outreach, type SalesOverviewProject, useSalesOverview } from './api'

// Most pressing first, the order the backend sorts projects in.
const GROUPS: { key: Outreach; label: string }[] = [
  { key: 'customer_waiting', label: 'Open with customer' },
  { key: 'delay', label: 'Delays to share' },
  { key: 'good_news', label: 'Good news' },
  { key: 'quiet', label: 'No recent update' },
  { key: 'on_track', label: 'On track' },
]

function contactDate(value: string) {
  const date = parseUTCDate(value)
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  })
}

function LastContact({ project }: { project: SalesOverviewProject }) {
  if (!project.last_contact_at) return <span className="text-muted-foreground">No contact yet</span>
  return (
    <span>
      <span className="text-muted-foreground">Last contact </span>
      {contactDate(project.last_contact_at)}
      <span className="text-muted-foreground">
        {' · '}
        {project.last_contact_from === 'customer' ? 'from them' : 'from us'}
      </span>
    </span>
  )
}

function Why({ project }: { project: SalesOverviewProject }) {
  return (
    <div className="space-y-0.5">
      {project.reason ? <p className="text-sm font-medium">{project.reason}</p> : null}
      <p className="text-xs tabular-nums">
        <LastContact project={project} />
      </p>
    </div>
  )
}

export function SalesOverviewPage() {
  const { showMyProjects, staffId } = useMyProjectsFilter()
  const [selectedStaffId, setSelectedStaffId] = useState<number>()
  const salesPersonId = showMyProjects ? staffId : selectedStaffId
  const { data, isLoading, isError, refetch } = useSalesOverview(salesPersonId)
  const [chosen, setChosen] = useState<Outreach>()

  const projects = data?.projects ?? []
  const byGroup = new Map(
    GROUPS.map((group) => [group.key, projects.filter((p) => p.outreach === group.key)]),
  )
  const firstWithProjects = GROUPS.find((group) => (byGroup.get(group.key)?.length ?? 0) > 0)
  const active = chosen ?? firstWithProjects?.key ?? GROUPS[0]!.key

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold tracking-tight">Sales Overview</h1>
        <StaffFilter
          value={salesPersonId}
          disabled={showMyProjects}
          onValueChange={setSelectedStaffId}
        />
      </div>

      {isError ? (
        <div className="flex flex-col items-center gap-3 py-12 text-sm text-muted-foreground">
          <p>Couldn't load projects.</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      ) : isLoading || !data ? (
        <div className="space-y-4">
          <Skeleton className="h-28 w-full rounded-xl" />
          <Skeleton className="h-9 w-full max-w-2xl" />
          <ProjectsTableSkeleton />
        </div>
      ) : (
        <>
          <Card>
            <CardContent>
              <StatusBar title="Projects" segments={segments(data.statuses)} />
            </CardContent>
          </Card>

          <Tabs value={active} onValueChange={(value) => setChosen(value as Outreach)}>
            <div className="-mx-1 overflow-x-auto px-1 pb-1.5">
              <TabsList variant="line">
                {GROUPS.map((group) => (
                  <TabsTrigger key={group.key} value={group.key} className="flex-none">
                    {group.label}{' '}
                    <span className="tabular-nums text-muted-foreground">
                      {byGroup.get(group.key)?.length ?? 0}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            {GROUPS.map((group) => (
              <TabsContent key={group.key} value={group.key}>
                {byGroup.get(group.key)?.length ? (
                  <ProjectsTable
                    items={byGroup.get(group.key) ?? []}
                    detail={(project) => <Why project={project} />}
                  />
                ) : (
                  <p className="py-12 text-center text-sm text-muted-foreground">No projects</p>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </>
      )}
    </div>
  )
}
